import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  TextInput,
  FlatList,
  Image,
  Platform,
} from 'react-native';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Ionicons } from '@expo/vector-icons';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';
import { useAssignmentStore } from '../../stores/assignmentStore';
import { useAuthStore } from '../../stores/authStore';
import { PERMISSIONS } from '../../constants/permissions';
import { CarReceivingForm } from '../../components/forms/CarReceivingForm';
import { CarDeliveryForm } from '../../components/forms/CarDeliveryForm';
import { Vehicle, Driver } from '../../types';
import { getVehicleImageByType, getRandomDriverImage } from '../../utils/sampleImages';
import { useResponsiveStyles } from '../../hooks/useResponsiveStyles';
import theme from '../../theme';

interface VehicleAssignmentScreenProps {
  navigation: any;
  route: {
    params?: {
      vehicleId?: string;
      driverId?: string;
      mode?: 'assign' | 'unassign'; // Explicit mode to determine intent
    };
  };
}

export const VehicleAssignmentScreen: React.FC<VehicleAssignmentScreenProps> = ({
  navigation,
  route,
}) => {
  // Add error boundary and console logs
  console.log('🚗 VehicleAssignmentScreen: Component initialized');
  const isWeb = Platform.OS === 'web';
  
  const { vehicles, fetchVehicles, assignVehicle, unassignVehicle } = useVehicleStore();
  const { drivers, fetchDrivers, assignDriver, unassignDriver } = useDriverStore();
  const { createAssignment, completeAssignment } = useAssignmentStore();
  
  console.log('🚗 VehicleAssignmentScreen: Stores loaded successfully');
  
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'selection' | 'receiving' | 'delivery'>('selection');
  const [searchQuery, setSearchQuery] = useState('');
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [customMonthlyRate, setCustomMonthlyRate] = useState<string>('');
  const [componentError, setComponentError] = useState<Error | null>(null);

  const { vehicleId, driverId, mode } = route.params || {};

  console.log('🚗 VehicleAssignmentScreen: State initialized, route params:', { vehicleId, driverId, mode });

  // Error boundary fallback
  if (componentError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: 'red', marginBottom: 10 }}>
          Vehicle Assignment Error
        </Text>
        <Text style={{ textAlign: 'center', marginBottom: 20 }}>
          {componentError?.message || 'An error occurred'}
        </Text>
        <TouchableOpacity 
          style={{ backgroundColor: '#007bff', padding: 10, borderRadius: 5 }}
          onPress={() => navigation.goBack()}
        >
          <Text style={{ color: 'white' }}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Debug: Log when vehicles data changes
  useEffect(() => {
    console.log('🚗 Assignment Screen - Vehicles updated:', vehicles.length, 'vehicles');
    if (vehicles.length > 0) {
      const statusDistribution = vehicles.reduce((acc, v) => {
        acc[v.carStatus || 'unknown'] = (acc[v.carStatus || 'unknown'] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('🚗 Assignment Screen - Status distribution:', statusDistribution);
    }
  }, [vehicles]);

  // Add refresh on focus (when user navigates back to this screen)
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      console.log('🔄 Assignment screen focused - refreshing data');
      fetchVehicles();
      fetchDrivers();
    });
    return unsubscribe;
  }, [navigation, fetchVehicles, fetchDrivers]);

  useEffect(() => {
    // Pre-select vehicle or driver based on route params
    console.log('🔍 VehicleAssignmentScreen: Route params:', { vehicleId, driverId, mode });
    
    if (vehicleId) {
      const vehicle = vehicles.find(v => v.id === vehicleId);
      console.log('🚗 Found vehicle:', vehicle?.licensePlate, 'Status:', vehicle?.carStatus, 'CurrentDriver:', vehicle?.currentDriverId);
      if (vehicle) {
        setSelectedVehicle(vehicle);
        // RESPECT THE MODE PARAMETER - don't override it based on status
        if (mode === 'unassign') {
          setIsUnassigning(true);
          console.log('🔄 Setting isUnassigning to: true (explicit unassign mode)');
          // Don't auto-jump to delivery - stay on selection screen for user review
        } else if (mode === 'assign') {
          setIsUnassigning(false);
          console.log('🔄 Setting isUnassigning to: false (explicit assign mode)');
          // Don't auto-jump to receiving - stay on selection screen for user review
        } else {
          // Only auto-detect if mode is not explicitly set
          const isVehicleAssigned = vehicle.carStatus === 'work' || vehicle.carStatus === 'assigned' || !!vehicle.currentDriverId;
          setIsUnassigning(isVehicleAssigned);
          console.log('🔄 Setting isUnassigning to:', isVehicleAssigned, '(auto-detected, no explicit mode)');
        }
      }
    }
    if (driverId) {
      const driver = drivers.find(d => d.id === driverId);
      console.log('👤 Found driver:', driver?.name, 'CurrentVehicle:', driver?.currentVehicleId);
      if (driver) {
        setSelectedDriver(driver);
        
        // RESPECT THE MODE PARAMETER - don't override it based on status
        if (mode === 'unassign') {
          setIsUnassigning(true);
          console.log('🔄 Setting isUnassigning to: true (explicit unassign mode)');
          
          // For unassignment mode, automatically find and set the driver's current vehicle
          if (driver.currentVehicleId) {
            const currentVehicle = vehicles.find(v => v.id === driver.currentVehicleId);
            if (currentVehicle) {
              console.log('🚗 Auto-selecting driver\'s current vehicle:', currentVehicle.licensePlate);
              setSelectedVehicle(currentVehicle);
              // Stay on selection screen - user must review and click Proceed to continue
              console.log('ℹ️ Staying on selection screen for user review before delivery form');
            }
          } else {
            // Driver should be assigned but currentVehicleId is missing - provide user-friendly options
            console.log('⚠️ Driver marked for unassignment but no currentVehicleId. Offering manual selection...');
            Alert.alert(
              'Vehicle Information Missing',
              'Unable to automatically detect this driver\'s assigned vehicle. Please select the vehicle to unassign manually or contact support if this driver should not have any vehicle assigned.',
              [
                {
                  text: 'Select Vehicle Manually',
                  onPress: () => {
                    // Allow manual vehicle selection for unassignment
                    console.log('👤 User chose to manually select vehicle for unassignment');
                  }
                },
                {
                  text: 'Cancel',
                  style: 'cancel',
                  onPress: () => navigation.goBack()
                }
              ]
            );
          }
        } else if (mode === 'assign') {
          setIsUnassigning(false);
          console.log('🔄 Setting isUnassigning to: false (explicit assign mode)');
          // Stay on selection screen - user must select vehicle and click Proceed
        } else {
          // Only auto-detect if mode is not explicitly set
          const isDriverAssigned = !!driver.currentVehicleId;
          setIsUnassigning(isDriverAssigned);
          console.log('🔄 Setting isUnassigning to:', isDriverAssigned, '(auto-detected, no explicit mode)');
          
          if (isDriverAssigned && driver.currentVehicleId) {
            const currentVehicle = vehicles.find(v => v.id === driver.currentVehicleId);
            if (currentVehicle) {
              console.log('🚗 Auto-selecting driver\'s current vehicle:', currentVehicle.licensePlate);
              setSelectedVehicle(currentVehicle);
              // Stay on selection screen for review
              console.log('ℹ️ Staying on selection screen for user review');
            }
          }
        }
      }
    }
  }, [vehicles, drivers, vehicleId, driverId, mode]);

  // Force refresh data
  const forceRefresh = async () => {
    console.log('🔄 Force refreshing assignment data...');
    setLoading(true);
    try {
      // Clear any cached data and refetch
      await fetchVehicles();
      await fetchDrivers();
      console.log('✅ Force refresh completed');
    } catch (error) {
      console.error('❌ Force refresh failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter available vehicles/drivers only
  const getAvailableVehicles = () => {
    console.log('🔍 Filtering vehicles - Total vehicles:', vehicles.length);
    console.log('🔍 isUnassigning flag:', isUnassigning);
    
    const available = vehicles.filter(vehicle => {
      // PRIMARY CHECK: Use car_status column directly
      const isAvailableByStatus = vehicle.carStatus === 'available';
      
      // SECONDARY VERIFICATION: Check current_driver_id
      const hasNoDriver = !vehicle.currentDriverId;
      
      // ADDITIONAL CHECK: Consider vehicles that should be available for assignment
      // Include vehicles that are not in special statuses and have no driver
      const isAssignable = isAvailableByStatus || (
        !['in_workshop', 'stop', 'cancellation', 'work', 'assigned'].includes(vehicle.carStatus || '') && hasNoDriver
      );
      
      const isAvailable = isAssignable;
      
      console.log(`� Vehicle ${vehicle.licensePlate}: Status=${vehicle.carStatus}, HasDriver=${!!vehicle.currentDriverId}, isAvailable=${isAvailable}`);
      
      return isAvailable;
    });
    
    console.log('✅ Assignment Screen - Available vehicles:', available.length, 'of', vehicles.length);
    console.log('📊 Status breakdown:', vehicles.reduce((acc: Record<string, number>, v) => {
      const key = `${v.carStatus}_${v.currentDriverId ? 'with_driver' : 'no_driver'}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {}));
    
    return available;
  };

  const getAvailableDrivers = () => {
    const available = drivers.filter(driver => {
      // Available drivers: 'active' status (working) AND no vehicle assigned
      // 'active' = working but currently unassigned
      // 'inactive' = not working at all (vacation, quit, etc.)
      const isAvailable = driver.status === 'active' && !driver.currentVehicleId;
      return isAvailable;
    });
    console.log('👨‍💼 getAvailableDrivers:', {
      totalDrivers: drivers.length,
      availableCount: available.length,
      sample: available.slice(0, 3).map(d => ({ name: d.name, status: d.status, hasVehicle: !!d.currentVehicleId }))
    });
    return available;
  };

  // Search functionality
  const getFilteredVehicles = () => {
    console.log('🔍 getFilteredVehicles called - isUnassigning:', isUnassigning);
    const availableVehicles = isUnassigning ? vehicles : getAvailableVehicles();
    console.log('🔍 getFilteredVehicles - availableVehicles count:', availableVehicles.length);
    
    if (!searchQuery) return availableVehicles;
    return availableVehicles.filter(vehicle =>
      vehicle.licensePlate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.makeModel?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getFilteredDrivers = () => {
    const availableDrivers = isUnassigning ? drivers : getAvailableDrivers();
    if (!searchQuery) return availableDrivers;
    return availableDrivers.filter(driver =>
      driver.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.licenseNumber?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleProceed = () => {
    console.log('🚀 handleProceed called. isUnassigning:', isUnassigning);
    
    if (isUnassigning) {
      // For unassignment, we need the driver and their vehicle (should be auto-selected)
      if (!selectedDriver) {
        Alert.alert('Error', 'Driver information is missing for unassignment');
        return;
      }
      
      if (!selectedVehicle) {
        Alert.alert('Error', 'Unable to determine the vehicle to unassign. Please contact support.');
        return;
      }
      
      console.log('➡️ Going to delivery step for unassignment');
      setCurrentStep('delivery');
    } else {
      // For assignment, we need both vehicle and driver to be selected
      if (!selectedVehicle || !selectedDriver) {
        Alert.alert('Error', 'Please select both a vehicle and a driver');
        return;
      }
      
      console.log('➡️ Going to receiving step for assignment');
      setCurrentStep('receiving');
    }
  };

  const handleReceivingFormSubmit = async (formData: any) => {
    console.log('🚗 VehicleAssignmentScreen: handleReceivingFormSubmit called');
    console.log('🚗 Received form data:', formData);
    console.log('🚗 Selected vehicle:', selectedVehicle);
    console.log('🚗 Selected driver:', selectedDriver);
    
    if (!selectedVehicle || !selectedDriver) {
      console.log('❌ Missing selection');
      if (isWeb) {
        window.alert('Error: Missing vehicle or driver selection');
      } else {
        Alert.alert('Error', 'Missing vehicle or driver selection');
      }
      return;
    }

    // Check CREATE_ASSIGNMENTS and CREATE_RECEIVING_FORMS permissions
    const hasCreateAssignment = useAuthStore.getState().hasPermission(PERMISSIONS.CREATE_ASSIGNMENTS);
    const hasCreateForm = useAuthStore.getState().hasPermission(PERMISSIONS.CREATE_RECEIVING_FORMS);
    
    if (!hasCreateAssignment || !hasCreateForm) {
      const deniedMessage = 'You do not have permission to create assignments or receiving forms.\\n\\nليس لديك صلاحية لإنشاء التعيينات أو نماذج الاستلام.';
      Alert.alert('Permission Denied / الإذن مرفوض', deniedMessage);
      return;
    }

    setLoading(true);
    try {
      console.log('🔄 Starting assignment process...');
      
      // 1. Create assignment history record first
      const finalMonthlyRate = customMonthlyRate ? parseFloat(customMonthlyRate) : (selectedVehicle.monthlyRentalRate || 0);
      
      const assignmentRecord = await createAssignment({
        vehicle_id: selectedVehicle.id,
        primary_driver_id: selectedDriver.id,
        assignment_date: formData.receivedAt || new Date().toISOString(),
        monthly_rent_rate: finalMonthlyRate,
        status: 'active',
        assignment_type: 'rental',
        is_shared: false,
      });
      
      console.log('✅ Assignment record created:', assignmentRecord);
      
      // 2. Create vehicle receiving form record
      const receivingFormData = {
        referenceNumber: `VRF-${new Date().getTime()}`,
        vehicleId: selectedVehicle.id,
        driverId: selectedDriver.id,
        assignmentDate: new Date(formData.receivedAt).toISOString().split('T')[0],
        assignmentTime: new Date(formData.receivedAt).toTimeString().split(' ')[0],
        rentAmount: finalMonthlyRate,
        currentMileage: formData.mileageIn,
        exteriorCondition: formData.exteriorCondition,
        interiorCondition: formData.interiorCondition,
        hasSpareKey: formData.keyStatus === 'received',
        hasDocuments: formData.documentsReceived?.length > 0 || true,
        hasDamages: formData.damages?.length > 0 || false,
        receivingNotes: formData.notes,
        staffName: formData.receivedBy,
        additionalNotes: `Fuel level: ${formData.fuelLevel}%. Key status: ${formData.keyStatus}`,
      };
      
      const { createVehicleReceivingForm } = await import('../../api/supabase');
      const receivingForm = await createVehicleReceivingForm(receivingFormData);
      console.log('✅ Vehicle receiving form created:', receivingForm);
      
      // 3. Create vehicle damage records if any
      if (formData.damages && formData.damages.length > 0) {
        const { createVehicleDamage } = await import('../../api/supabase');
        for (const damage of formData.damages) {
          const damageData = {
            receivingFormId: receivingForm.id,
            damageType: damage.type || 'general',
            damageLocation: damage.location || 'unspecified',
            damageDescription: damage.description,
            photoUrl: damage.photoUrl,
          };
          await createVehicleDamage(damageData);
          console.log('✅ Vehicle damage record created for:', damage.location);
        }
      }
      
      // 4. Perform the assignment in both stores
      await Promise.all([
        assignVehicle(selectedVehicle.id, selectedDriver.id),
        assignDriver(selectedDriver.id, selectedVehicle.id)
      ]);
      
      console.log('✅ Assignment successful');
      
      // Success message and smart navigation back to origin
      const successMessage = `Vehicle ${selectedVehicle.licensePlate} has been successfully assigned to ${selectedDriver.name}!\n\nAll forms have been saved to the database.`;
      
      // Determine where to navigate based on how user got here
      const navigateBack = () => {
        if (driverId) {
          // User came from drivers screen - go back to drivers
          navigation.navigate('DriversView');
        } else if (vehicleId) {
          // User came from vehicles screen - go back to vehicles
          navigation.navigate('VehicleDatabase');
        } else {
          // Fallback to dashboard
          navigation.navigate('Dashboard');
        }
      };
      
      if (isWeb) {
        window.alert('Assignment Complete!\n\n' + successMessage);
        // Reset form state before navigation
        setSelectedVehicle(null);
        setSelectedDriver(null);
        setCurrentStep('selection');
        navigateBack();
      } else {
        Alert.alert(
          'Assignment Complete!', 
          successMessage,
          [
            {
              text: 'OK',
              onPress: () => {
                // Reset form state before navigation
                setSelectedVehicle(null);
                setSelectedDriver(null);
                setCurrentStep('selection');
                
                // Navigate back
                navigateBack();
              },
              style: 'default'
            }
          ],
          { cancelable: false } // Prevent accidental dismissal
        );
      }
    } catch (error) {
      console.error('❌ Assignment error:', error);
      if (isWeb) {
        window.alert(`Error: Failed to assign vehicle: ${error.message}. Please try again.`);
      } else {
        Alert.alert('Error', `Failed to assign vehicle: ${error.message}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeliveryFormSubmit = async (formData: any) => {
    console.log('🚗 VehicleAssignmentScreen: handleDeliveryFormSubmit called');
    console.log('🚗 Received form data:', formData);
    console.log('🚗 Selected vehicle:', selectedVehicle);
    console.log('🚗 Selected driver:', selectedDriver);
    
    if (!selectedVehicle || !selectedDriver) {
      console.log('❌ Missing selection');
      if (isWeb) {
        window.alert('Error: Missing vehicle or driver selection');
      } else {
        Alert.alert('Error', 'Missing vehicle or driver selection');
      }
      return;
    }

    // Check CREATE_DELIVERY_FORMS permission
    const hasCreateForm = useAuthStore.getState().hasPermission(PERMISSIONS.CREATE_DELIVERY_FORMS);
    
    if (!hasCreateForm) {
      const deniedMessage = 'You do not have permission to create delivery forms.\\n\\nليس لديك صلاحية لإنشاء نماذج التسليم.';
      Alert.alert('Permission Denied / الإذن مرفوض', deniedMessage);
      return;
    }

    setLoading(true);
    try {
      console.log('🔄 Starting unassignment process...');
      
      // 1. Complete all assignment records and create delivery record using enhanced function
      const { completeVehicleUnassignment } = useAssignmentStore.getState();
      await completeVehicleUnassignment(selectedVehicle.id, selectedDriver.id, formData);
      
      // 2. Perform the unassignment in both stores (vehicle and driver status)
      await Promise.all([
        unassignVehicle(selectedVehicle.id),
        unassignDriver(selectedDriver.id)
      ]);
      
      console.log('✅ Complete unassignment successful');
      
      // Success message and smart navigation back to origin
      const successMessage = `Vehicle ${selectedVehicle.licensePlate} has been successfully unassigned from ${selectedDriver.name}!\n\nAll delivery information has been saved and assignment records completed.`;
      
      // Determine where to navigate based on how user got here
      const navigateBack = () => {
        if (driverId) {
          // User came from drivers screen - go back to drivers
          navigation.navigate('DriversView');
        } else if (vehicleId) {
          // User came from vehicles screen - go back to vehicles
          navigation.navigate('VehicleDatabase');
        } else {
          // Fallback to dashboard
          navigation.navigate('Dashboard');
        }
      };
      
      if (isWeb) {
        window.alert('Unassignment Complete!\n\n' + successMessage);
        navigateBack();
      } else {
        Alert.alert(
          'Unassignment Complete!', 
          successMessage,
          [
            {
              text: 'OK',
              onPress: navigateBack,
              style: 'default'
            }
          ]
        );
      }
    } catch (error) {
      console.error('❌ Unassignment error:', error);
      if (isWeb) {
        window.alert(`Error: Failed to unassign vehicle: ${error.message}. Please try again.`);
      } else {
        Alert.alert('Error', `Failed to unassign vehicle: ${error.message}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderVehicleCard = ({ item: vehicle }: { item: Vehicle }) => (
    <TouchableOpacity
      style={[
        styles.entityCard,
        selectedVehicle?.id === vehicle.id && styles.selectedCard
      ]}
      onPress={() => setSelectedVehicle(vehicle)}
    >
      <Image
        source={{ uri: vehicle.image_url || getVehicleImageByType(vehicle.makeModel || '') }}
        style={styles.entityImage}
        resizeMode="cover"
      />
      <View style={styles.entityInfo}>
        <Text style={styles.entityTitle} numberOfLines={1}>{vehicle.licensePlate}</Text>
        <Text style={styles.entitySubtitle} numberOfLines={1}>{vehicle.makeModel}</Text>
        <Text style={styles.entityDetail} numberOfLines={1}>Year: {vehicle.year}</Text>
        <Text style={[
          styles.entityDetail,
          { color: vehicle.carStatus === 'available' ? theme.colors.success : theme.colors.warning }
        ]} numberOfLines={1}>
          Status: {vehicle.carStatus}
        </Text>
      </View>
      {selectedVehicle?.id === vehicle.id && (
        <View style={styles.checkmarkContainer}>
          <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
        </View>
      )}
    </TouchableOpacity>
  );

  const renderDriverCard = ({ item: driver }: { item: Driver }) => (
    <TouchableOpacity
      style={[
        styles.entityCard,
        selectedDriver?.id === driver.id && styles.selectedCard
      ]}
      onPress={() => setSelectedDriver(driver)}
    >
      <Image
        source={{ uri: driver.image_url || getRandomDriverImage() }}
        style={styles.entityImage}
        resizeMode="cover"
      />
      <View style={styles.entityInfo}>
        <Text style={styles.entityTitle} numberOfLines={1}>{driver.name}</Text>
        <Text style={styles.entitySubtitle} numberOfLines={1}>{driver.licenseNumber}</Text>
        <Text style={styles.entityDetail} numberOfLines={1}>Phone: {driver.phoneNumber}</Text>
        <Text style={[
          styles.entityDetail,
          { color: driver.status === 'inactive' ? theme.colors.success : theme.colors.warning }
        ]} numberOfLines={1}>
          Status: {driver.status} {driver.status === 'inactive' ? '(Available)' : '(Assigned)'}
        </Text>
      </View>
      {selectedDriver?.id === driver.id && (
        <View style={styles.checkmarkContainer}>
          <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
        </View>
      )}
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  if (currentStep === 'receiving') {
    return (
      <CarReceivingForm
        vehicle={selectedVehicle!}
        driver={selectedDriver!}
        onSubmit={handleReceivingFormSubmit}
        onCancel={() => setCurrentStep('selection')}
      />
    );
  }

  if (currentStep === 'delivery') {
    return (
      <CarDeliveryForm
        vehicle={selectedVehicle!}
        driver={selectedDriver!}
        onSubmit={handleDeliveryFormSubmit}
        onCancel={() => setCurrentStep('selection')}
      />
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>
            {isUnassigning ? 'Unassign Vehicle' : 'Assign Vehicle'}
          </Text>
          <Text style={styles.headerSubtitle}>
            {isUnassigning 
              ? 'Complete delivery form to unassign' 
              : 'Select vehicle and driver, then complete receiving form'
            }
          </Text>
        </View>
        <TouchableOpacity 
          style={styles.refreshButton}
          onPress={forceRefresh}
          disabled={loading}
        >
          <Ionicons name="refresh" size={24} color={theme.colors.blue600} />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content}>
        {/* Selected Vehicle Info */}
        {selectedVehicle && (
          <Card style={styles.selectedSection}>
            <Text style={styles.sectionTitle}>Selected Vehicle</Text>
            <View style={styles.selectedInfo}>
              <Image
                source={{ uri: selectedVehicle.image_url || getVehicleImageByType(selectedVehicle.makeModel || '') }}
                style={styles.selectedImage}
                resizeMode="cover"
              />
              <View style={styles.selectedDetails}>
                <Text style={styles.selectedTitle}>{selectedVehicle.licensePlate}</Text>
                <Text style={styles.selectedSubtitle}>{selectedVehicle.makeModel}</Text>
                <Text style={styles.selectedDetail}>Year: {selectedVehicle.year}</Text>
                <Text style={styles.selectedDetail}>Status: {selectedVehicle.carStatus}</Text>
              </View>
            </View>
          </Card>
        )}

        {/* Selected Driver Info */}
        {selectedDriver && (
          <Card style={styles.selectedSection}>
            <Text style={styles.sectionTitle}>Selected Driver</Text>
            <View style={styles.selectedInfo}>
              <Image
                source={{ uri: selectedDriver.image_url || getRandomDriverImage() }}
                style={styles.selectedImage}
                resizeMode="cover"
              />
              <View style={styles.selectedDetails}>
                <Text style={styles.selectedTitle}>{selectedDriver.name}</Text>
                <Text style={styles.selectedSubtitle}>{selectedDriver.licenseNumber}</Text>
                <Text style={styles.selectedDetail}>Phone: {selectedDriver.phoneNumber}</Text>
                <Text style={styles.selectedDetail}>Status: {selectedDriver.status}</Text>
              </View>
            </View>
          </Card>
        )}

        {/* Rate Configuration - Show when both vehicle and driver are selected */}
        {selectedVehicle && selectedDriver && (
          <Card style={styles.selectedSection}>
            <Text style={styles.sectionTitle}>Monthly Rate Configuration</Text>
            <View style={styles.rateConfigSection}>
              <View style={styles.rateInfo}>
                <Text style={styles.rateLabel}>Vehicle Default Rate:</Text>
                <Text style={styles.rateValue}>AED {selectedVehicle.monthlyRentalRate || 0}</Text>
              </View>
              
              <View style={styles.customRateContainer}>
                <Text style={styles.inputLabel}>Custom Monthly Rate (Optional):</Text>
                <TextInput
                  style={styles.rateInput}
                  placeholder="Enter custom monthly rate"
                  value={customMonthlyRate}
                  onChangeText={setCustomMonthlyRate}
                  keyboardType="numeric"
                />
                <Text style={styles.inputHelper}>
                  Leave empty to use vehicle default rate (AED {selectedVehicle.monthlyRentalRate || 0})
                </Text>
              </View>
              
              <View style={styles.rateInfo}>
                <Text style={styles.rateLabel}>Final Rate:</Text>
                <Text style={styles.finalRateValue}>
                  AED {customMonthlyRate ? parseFloat(customMonthlyRate) || 0 : selectedVehicle.monthlyRentalRate || 0}
                </Text>
              </View>
            </View>
          </Card>
        )}

        {/* Vehicle Selection - Only show if no vehicle selected */}
        {!selectedVehicle && (
          <Card style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                Select Vehicle ({isUnassigning ? 'All' : 'Available Only'})
              </Text>
              <Text style={styles.countText}>
                {getFilteredVehicles().length} vehicles
              </Text>
            </View>
            
            {/* Search for Vehicles */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={16} color={theme.colors.gray400} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search by license plate number..."
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>

            <FlatList
              data={getFilteredVehicles().slice(0, 8)} // Show only 2 rows (4 columns x 2 rows)
              renderItem={renderVehicleCard}
              keyExtractor={(item) => item.id}
              key={`vehicles-${vehicles.length}-${Date.now()}`} // Force re-render when data changes
              numColumns={4}
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={
                <Text style={styles.emptyText}>
                  {isUnassigning ? 'No vehicles found' : 'No available vehicles found'}
                </Text>
              }
              ListFooterComponent={
                getFilteredVehicles().length > 8 ? (
                  <TouchableOpacity 
                    style={styles.showMoreButton}
                    onPress={() => {/* TODO: Implement show more */}}
                  >
                    <Text style={styles.showMoreText}>
                      Show {getFilteredVehicles().length - 8} more vehicles...
                    </Text>
                  </TouchableOpacity>
                ) : null
              }
            />
          </Card>
        )}

        {/* Driver Selection - Only show if no driver selected AND vehicle is selected */}
        {!selectedDriver && selectedVehicle && (
          <Card style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                Select Driver ({isUnassigning ? 'All' : 'Available Only'})
              </Text>
              <Text style={styles.countText}>
                {getFilteredDrivers().length} drivers
              </Text>
            </View>
            
            {/* Search for Drivers */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={16} color={theme.colors.gray400} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search by driver name..."
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>

            <FlatList
              data={getFilteredDrivers().slice(0, 8)} // Show only 2 rows (4 columns x 2 rows)
              renderItem={renderDriverCard}
              keyExtractor={(item) => item.id}
              numColumns={4}
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={
                <Text style={styles.emptyText}>
                  {isUnassigning ? 'No drivers found' : 'No available drivers found'}
                </Text>
              }
              ListFooterComponent={
                getFilteredDrivers().length > 8 ? (
                  <TouchableOpacity 
                    style={styles.showMoreButton}
                    onPress={() => {/* TODO: Implement show more */}}
                  >
                    <Text style={styles.showMoreText}>
                      Show {getFilteredDrivers().length - 8} more drivers...
                    </Text>
                  </TouchableOpacity>
                ) : null
              }
            />
          </Card>
        )}
      </ScrollView>

      {/* Action Buttons */}
      {selectedVehicle && selectedDriver && (
        <View style={styles.footer}>
          <Button
            title={isUnassigning ? "Proceed to Delivery" : "Proceed to Assignment"}
            onPress={handleProceed}
            style={styles.proceedButton}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  header: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    marginRight: theme.spacing.md,
  },
  refreshButton: {
    marginLeft: theme.spacing.md,
    padding: theme.spacing.sm,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  content: {
    flex: 1,
    padding: theme.spacing.lg,
  },
  selectedSection: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  section: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  countText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    fontWeight: '500' as any,
  },
  searchContainer: {
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchIcon: {
    marginRight: theme.spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: '100%',
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  selectedInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedImage: {
    width: 80,
    height: 80,
    borderRadius: theme.borderRadius.md,
    marginRight: theme.spacing.md,
  },
  selectedDetails: {
    flex: 1,
  },
  selectedTitle: {
    fontSize: 16,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  selectedSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  selectedDetail: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 1,
  },
  entityCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    margin: 4,
    borderWidth: 2,
    borderColor: 'transparent',
    flex: 1,
    minWidth: 0, // Important for proper flexbox sizing
    maxWidth: '23%', // Roughly 1/4 width accounting for margins
    ...theme.shadows.sm,
  },
  selectedCard: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primary + '10',
  },
  entityImage: {
    width: '100%',
    height: 60, // Smaller height for grid
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing.xs,
  },
  entityInfo: {
    flex: 1,
    minHeight: 60, // Consistent card height
  },
  entityTitle: {
    fontSize: 12, // Smaller font for grid
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  entitySubtitle: {
    fontSize: 10, // Smaller font for grid
    color: theme.colors.textSecondary,
    marginTop: 1,
  },
  entityDetail: {
    fontSize: 9, // Smaller font for grid
    color: theme.colors.textSecondary,
    marginTop: 1,
  },
  checkmarkContainer: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: theme.colors.white,
    borderRadius: 10,
    padding: 2,
  },
  showMoreButton: {
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginTop: theme.spacing.sm,
    alignItems: 'center',
  },
  showMoreText: {
    color: theme.colors.primary,
    fontSize: 14,
    fontWeight: '500' as any,
  },
  emptyText: {
    textAlign: 'center',
    color: theme.colors.textSecondary,
    fontSize: 14,
    marginTop: theme.spacing.lg,
  },
  footer: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  proceedButton: {
    width: '100%',
  },
  rateConfigSection: {
    gap: 16,
  },
  rateInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  rateLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  rateValue: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  finalRateValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  customRateContainer: {
    gap: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text,
  },
  rateInput: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: theme.colors.background,
  },
  inputHelper: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
});
